import { type JWTPayload } from 'jose';

import type { AuthRedirectPage } from './auth/routes.ts';
import type { Commitment } from './proxy-api/commitment.ts';
import type { GiftLimits } from './proxy-api/giftLimits.ts';
import type { PaymentMethodItem } from './proxy-api/paymentMethods.ts';
import type {
  AddressDetails,
  Supporter,
  SupporterPostResponse,
} from './proxy-api/supporter.ts';

/** Authentication object that we store in Redis. Note: Not every attribute here may be present on the return object, it depends on whether we have stored some of these values or not. However, the access, id, refresh, auth user attributes are guaranteed to be stored during authentication. The rest depends on the proxy api to store these values. */
export interface AuthUserCacheAttributes {
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
  authUserGlobalId: string;
  authUserInternalId: string;
  authUserPrimaryContactId?: string;
  authUsername: string;
  preferredUsername: string;
  supporter?: Supporter;
  addresses?: AddressDetails;
  paymentMethods?: PaymentMethodItem[];
  giftLimits?: GiftLimits | null;
  commitments?: Commitment[];
}

/** Authentication response that we return to the client upon authentication, without the tokens Attributes _could_ include supporter information. */
export type AuthUserCacheAttributesWithNoTokens = Omit<
  AuthUserCacheAttributes,
  'accessToken' | 'idToken' | 'refreshToken'
>;

export type AuthClientResponse = Pick<
  AuthUserCacheAttributes,
  | 'authUserGlobalId'
  | 'authUserInternalId'
  | 'authUserPrimaryContactId'
  | 'authUsername'
  | 'supporter'
> & {
  success: true;
  sessionJwt?: string;
};

export interface AuthJwtPayload extends JWTPayload {
  iss: string;
  sub: string;
  sessionId: string;
  supporterGlobalId: string;
  supporterInternalId: string;
  username: string;
  preferredUsername?: string;
}

export enum AuthScenario {
  //
  MULTIPLE_ACCOUNTS_FOUND = 'MULTIPLE_ACCOUNTS_FOUND',
  NEED_MORE_INFO = 'NEED_MORE_INFO',
  //
  ACCOUNT_NOT_FOUND = 'ACCOUNT_NOT_FOUND',
  ACCOUNT_CONFIRMED = 'ACCOUNT_CONFIRMED',
  ACCOUNT_CONFIRMED_AND_AUTHENTICATED = 'ACCOUNT_CONFIRMED_AND_AUTHENTICATED',
  ACCOUNT_NOT_CONFIRMED = 'ACCOUNT_NOT_CONFIRMED',
  ACCOUNT_ALREADY_EXISTS = 'ACCOUNT_ALREADY_EXISTS',
  //
  REQUIRED_PASSWORD_RESET = 'REQUIRED_PASSWORD_RESET',
  REQUIRED_EMAIL_VERIFICATION = 'REQUIRED_EMAIL_VERIFICATION',
  REQUIRED_USERNAME_RESET = 'REQUIRED_USERNAME_RESET',
  //
  EXPIRED_CODE = 'EXPIRED_CODE',
  INVALID_CODE = 'INVALID_CODE',
  INVALID_PASSWORD = 'INVALID_PASSWORD',
  //
  USERNAME_ALREADY_IN_USE = 'USERNAME_ALREADY_IN_USE',
  WEB_ACCOUNT_ALREADY_EXISTS = 'WEB_ACCOUNT_ALREADY_EXISTS',
  WEB_ACCOUNT_BY_EMAIL_ALREADY_EXISTS = 'WEB_ACCOUNT_BY_EMAIL_ALREADY_EXISTS',
  //
  MISSING_FIELDS = 'MISSING_FIELDS',
  NOT_AUTHORIZED = 'NOT_AUTHORIZED',
  TOO_MANY_ATTEMPTS = 'TOO_MANY_ATTEMPTS',
  USER_STATUS_NOT_RESOLVABLE = 'USER_STATUS_NOT_RESOLVABLE',
  USER_SUPPORTER_NOT_FOUND = 'USER_SUPPORTER_NOT_FOUND',
  INVALID_SESSION = 'INVALID_SESSION',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}

export enum UserStatusType {
  ARCHIVED = 'ARCHIVED',
  COMPROMISED = 'COMPROMISED',
  CONFIRMED = 'CONFIRMED',
  EXTERNAL_PROVIDER = 'EXTERNAL_PROVIDER',
  FORCE_CHANGE_PASSWORD = 'FORCE_CHANGE_PASSWORD',
  RESET_REQUIRED = 'RESET_REQUIRED',
  UNCONFIRMED = 'UNCONFIRMED',
  UNKNOWN = 'UNKNOWN',
}

export interface AuthInternalError {
  type?: AuthScenario;
  log?: string;
  message: string;
  status: number;
}

export interface AuthInternalResponseBody {
  type?: AuthScenario;
  authUserGlobalId?: string;
  authUserInternalId?: string;
  authUserPrimaryContactId?: string;
  authUsername?: string;
  supporter?: Supporter | SupporterPostResponse;
  sessionJwt?: string;
  email?: string;
  nextStep?: AuthRedirectPage;
}

/**
 * Types of the login state of current user
 */
export enum UserLoginState {
  /**
   * User is not logged in.
   */
  Guest = 'Guest',
  /**
   * User is logged in.
   */
  LoggedIn = 'LoggedIn',
}

export type AuthSignupSessionCacheAttributes = {
  email: string;
  nextStep: AuthRedirectPage;
  internalId?: string;
  app?: string;
  returnUrl?: string;
  validated?: boolean;
};
